import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { LuExternalLink, LuPanelRight } from 'react-icons/lu';
import { LuX } from 'react-icons/lu';
import { BsArrowsAngleExpand, BsArrowsAngleContract, BsCopy, BsCheck2 } from 'react-icons/bs';
import { FaSpinner } from 'react-icons/fa';
import { IoPlayOutline } from 'react-icons/io5';
import { NODE_THEMES } from './NodeTheme';
import { NodeFormProvider } from './NodeFormKit';
import { questionToast } from '../../../plugins/toastAll';

// Id del nodo que se está editando. Lo publica NodeEditorPanel para no tener que
// pasarlo como prop a cada uno de los editores.
export const EditorNodeIdContext = createContext(null);
export const StartNodeContext = createContext(null);
export const EditorDockContext = createContext(null);

// "0nVSrPCW4v-basicAnswer" -> "0nVSrPCW4v": el sufijo repite el tipo que ya se muestra.
export const shortNodeId = (id) => String(id || '').replace(/-[A-Za-z]+$/, '');

export const copyNodeId = (id) => {
  if (!id || !navigator?.clipboard) return Promise.resolve(false);
  return navigator.clipboard.writeText(id).then(() => true, () => false);
};

const NodeIdBadge = ({ nodeId, theme }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!(await copyNodeId(nodeId))) return;
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={handleCopy}
      data-tooltip-id="my-tooltip"
      data-tooltip-content={copied ? 'Copiado' : `Copiar ID: ${nodeId}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '0 5px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: copied ? '#00A884' : theme.textTertiary,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '12px',
        letterSpacing: 0,
        textTransform: 'none',
        cursor: 'pointer',
        lineHeight: 1.3,
      }}
    >
      #{shortNodeId(nodeId)}
      {copied ? <BsCheck2 style={{ fontSize: '12px' }} /> : <BsCopy style={{ fontSize: '11px' }} />}
    </button>
  );
};

// Botón fantasma del header: sin fondo en reposo, tinte sutil al pasar el cursor.
const GhostIconButton = ({ theme, hoverColor, onClick, children, ...rest }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '30px',
        padding: 0,
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: hover ? theme.inputBg : 'transparent',
        color: hover ? hoverColor : theme.textSecondary,
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

// Chrome compartido (header + body wrapper + footer + toasts) reutilizado por
// los paneles de edición de cada tipo de nodo. Cada tipo solo aporta su icono,
// color de marca, nombre de tipo y el formulario (children).
// Props opcionales para variaciones por tipo:
// - isExpanded/setIsExpanded: muestra el botón expandir/contraer si se pasan.
// - canRemove: oculta el botón de eliminar (ej. nodo inicial).
// - saveDisabled: deshabilita Guardar mientras se guarda.
// - toastAutoClose: tiempo de autocierre del ToastContainer.
// - toast: reemplaza el ToastContainer estándar por uno propio del tipo.
const NodeEditorChrome = ({
  icon: Icon,
  color,
  typeName,
  title,
  onRemove,
  onMouseDown,
  isDragging,
  onClose,
  formId,
  saving,
  isDark,
  isExpanded,
  setIsExpanded,
  canRemove = true,
  saveDisabled = false,
  getIsDirty,
  toastAutoClose = 52000,
  toast,
  children,
}) => {
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const nodeId = useContext(EditorNodeIdContext);
  const startNode = useContext(StartNodeContext);
  const dock = useContext(EditorDockContext);
  const isStartNode = Boolean(startNode?.isStart);
  const [closeHover, setCloseHover] = useState(false);
  const formContext = useMemo(() => ({ theme, color }), [theme, color]);

  const handleClose = async () => {
    if (getIsDirty?.()) {
      const confirmed = await questionToast(
        {
          isFetch: false,
          fetch: {},
          title: 'Este nodo tiene cambios sin guardar',
          text: 'Si cierras ahora se perderán.',
          icon: 'warning',
          buttons: { success: 'Cerrar sin guardar', cancel: 'Seguir editando' },
        },
        () => {}
      );
      if (!confirmed) return;
    }
    setTimeout(() => {
      onClose();
    }, 200);
  };

  return (
    <>
      {/* Los formularios de los 11 tipos usan markup Bootstrap. En vez de
          reescribirlos uno por uno, se tematizan aquí, en un solo sitio. */}
      <style>{`
        .node-editor-body label {
          font-size: 12.5px;
          font-weight: 600;
          color: ${theme.textSecondary};
          margin-bottom: 5px;
        }
        /* Excepción: los toggles (cmn-toggle) usan el <label> como interruptor
           dibujado con background, no como texto. No debe heredar lo anterior. */
        .node-editor-body .cmn-toggle + label {
          margin-bottom: 0;
        }
        /* Excepción: labels que deben llevar el color del tipo (tiles de
           multimedia del NodeFormKit), no el gris de los labels de campo. */
        .node-editor-body label.nfk-accent-label {
          color: ${color} !important;
          margin-bottom: 0;
        }
        .node-editor-body .form-control,
        .node-editor-body input[type='text'],
        .node-editor-body input[type='number'],
        .node-editor-body input[type='url'],
        .node-editor-body select,
        .node-editor-body textarea {
          background-color: ${theme.inputBg};
          border: 1px solid ${theme.border};
          border-radius: 10px;
          color: ${theme.textPrimary};
          font-size: 14px;
          box-shadow: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .node-editor-body .form-control:focus,
        .node-editor-body input:focus,
        .node-editor-body select:focus,
        .node-editor-body textarea:focus {
          background-color: ${theme.inputBg};
          border-color: #5DCCED;
          box-shadow: none;
          color: ${theme.textPrimary};
          outline: none;
        }
        .node-editor-body .form-control::placeholder,
        .node-editor-body textarea::placeholder {
          color: ${theme.textTertiary};
        }
        /* react-select trae .form-control en el wrapper; su propio control ya
           dibuja la caja, así que el wrapper no debe pintar una segunda. */
        .node-editor-body .basic-multi-select {
          background-color: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
          height: auto;
          box-shadow: none;
        }
        .node-editor-body small,
        .node-editor-body .form-text {
          color: ${theme.textTertiary};
          font-size: 12px;
        }

        /* Scrollbar del panel y de los campos internos, en el color del tipo */
        .node-editor-body,
        .node-editor-body textarea,
        .node-editor-body .form-control {
          scrollbar-width: thin;
          scrollbar-color: ${theme.borderStrong} transparent;
        }
        .node-editor-body::-webkit-scrollbar,
        .node-editor-body textarea::-webkit-scrollbar,
        .node-editor-body .form-control::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .node-editor-body::-webkit-scrollbar-track,
        .node-editor-body textarea::-webkit-scrollbar-track,
        .node-editor-body .form-control::-webkit-scrollbar-track {
          background: transparent;
        }
        .node-editor-body::-webkit-scrollbar-thumb,
        .node-editor-body textarea::-webkit-scrollbar-thumb,
        .node-editor-body .form-control::-webkit-scrollbar-thumb {
          background-color: transparent;
          border-radius: 6px;
        }
        .node-editor-body:hover::-webkit-scrollbar-thumb,
        .node-editor-body textarea:hover::-webkit-scrollbar-thumb,
        .node-editor-body .form-control:hover::-webkit-scrollbar-thumb {
          background-color: ${theme.borderStrong};
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: '64px',
          padding: '8px 14px',
          borderBottom: `1px solid ${theme.border}`,
          backgroundColor: theme.panelBg,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
        onMouseDown={onMouseDown}
      >
        <GhostIconButton
          theme={theme}
          hoverColor={theme.textPrimary}
          onClick={handleClose}
          aria-label="Cerrar"
          data-tooltip-id="my-tooltip"
          data-tooltip-content="Cerrar"
        >
          <LuX style={{ fontSize: '18px', color: theme.textPrimary }} />
        </GhostIconButton>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            borderRadius: '8px',
            backgroundColor: color,
            flexShrink: 0,
          }}
        >
          <Icon style={{ fontSize: '16px', color: '#FFFFFF' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '17px',
              fontWeight: 600,
              color: theme.textPrimary,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={title}
          >
            {title}
          </div>
          {typeName && <div style={{ fontSize: '12.5px', color: theme.textTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typeName}</div>}
        </div>
        {nodeId && <NodeIdBadge nodeId={nodeId} theme={theme} />}

        <div className="d-flex align-items-center" style={{ gap: '2px', flexShrink: 0 }}>
          {dock && (
            <GhostIconButton
              theme={theme}
              hoverColor={theme.textPrimary}
              onClick={dock.toggle}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={dock.floating ? 'Fijar a la derecha' : 'Soltar y mover'}
            >
              {dock.floating ? <LuPanelRight style={{ fontSize: '16px' }} /> : <LuExternalLink style={{ fontSize: '15px' }} />}
            </GhostIconButton>
          )}
          {setIsExpanded && (
            <GhostIconButton
              theme={theme}
              hoverColor={theme.textPrimary}
              onClick={() => setIsExpanded(!isExpanded)}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={isExpanded ? 'Contraer' : 'Expandir'}
            >
              {isExpanded ? (
                <BsArrowsAngleContract style={{ fontSize: '15px' }} />
              ) : (
                <BsArrowsAngleExpand style={{ fontSize: '15px' }} />
              )}
            </GhostIconButton>
          )}
          {startNode?.canBeStart && !isStartNode && (
            <GhostIconButton
              theme={theme}
              hoverColor={color}
              onClick={startNode.onMakeStart}
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Marcar como nodo de inicio"
            >
              <IoPlayOutline style={{ fontSize: '16px' }} />
            </GhostIconButton>
          )}
        </div>
      </div>

      <div
        id="content-list-chats"
        className="card-body node-editor-body"
        style={{
          overflowY: 'auto',
          backgroundColor: theme.panelBg,
          color: theme.textPrimary,
        }}
      >
        <NodeFormProvider value={formContext}>{children}</NodeFormProvider>
      </div>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: theme.panelBg,
          borderTop: `1px solid ${theme.border}`,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        {canRemove && !isStartNode && (
          <button
            type="button"
            onClick={onRemove}
            style={{ padding: '2px 4px', border: 'none', background: 'none', color: theme === NODE_THEMES.dark ? '#ff7a8c' : '#d6102f', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
          >
            Eliminar nodo
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onMouseEnter={() => setCloseHover(true)}
          onMouseLeave={() => setCloseHover(false)}
          onClick={handleClose}
          style={{
            padding: '9px 15px',
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '9px',
            border: `1px solid ${theme.border}`,
            backgroundColor: closeHover ? theme.inputBg : 'transparent',
            color: theme.textSecondary,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form={formId}
          disabled={saveDisabled}
          style={{
            padding: '9px 18px',
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '9px',
            border: 'none',
            backgroundColor: '#0a7ba5',
            color: '#FFFFFF',
            cursor: saveDisabled ? 'default' : 'pointer',
            opacity: saveDisabled ? 0.6 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          {saving ? <FaSpinner className="fa-spin" /> : <span>Guardar</span>}
        </button>
      </div>

      {toast !== undefined ? (
        toast
      ) : (
        <ToastContainer
          position="top-center"
          autoClose={toastAutoClose}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          theme={JSON.parse(localStorage.getItem('THEME')) ? 'dark' : 'light'}
          toastClassName="custom-toast"
          bodyClassName="custom-toast-body"
        />
      )}
    </>
  );
};

export default NodeEditorChrome;
